import { ForwardedRef, forwardRef, HTMLProps, useLayoutEffect, useMemo, useRef } from 'react';
import { observer } from 'mobx-react';
import cls from 'classnames';
import DOMPurify, { Config } from 'dompurify';
import mergeRefs from 'merge-refs';

import styles from './RichText.module.css';
import { clampToLines } from './RichText.utils';

export interface RichTextProps extends Omit<HTMLProps<HTMLDivElement>, 'dangerouslySetInnerHTML'> {
  value: string;
  className?: string;
  config?: Config;
  clampLines?: number;
}

function RichText(props: RichTextProps, ref: ForwardedRef<HTMLDivElement>) {
  const { value, className, config, clampLines, ...rest } = props;
  const containerRef = useRef<HTMLDivElement>(null);

  // DOMPurify unsupported only in legacy browsers which cutwise not support anyway
  const html = useMemo(() => {
    return DOMPurify.isSupported ? DOMPurify.sanitize(value, config) : '';
  }, [value, config]);

  useLayoutEffect(() => {
    const container = containerRef.current;

    if (!container) {
      return;
    }

    if (!clampLines) {
      // React skips re-applying dangerouslySetInnerHTML when the html string is unchanged,
      // so a previous clamp's DOM mutation has to be undone explicitly here
      container.innerHTML = html;
      container.style.removeProperty('max-height');
      container.style.removeProperty('overflow');

      return;
    }

    let isCancelled = false;
    let lastWidth = container.clientWidth;

    const clamp = () => {
      if (isCancelled) {
        return;
      }

      // the tail is cut out of the DOM, so the full markup has to be restored before every recalculation
      container.innerHTML = html;
      clampToLines(container, clampLines);
    };

    clamp();

    const observer = new ResizeObserver(() => {
      if (container.clientWidth === lastWidth) {
        return;
      }

      lastWidth = container.clientWidth;
      clamp();
    });

    observer.observe(container);

    // the first pass may run on a fallback font, which gives a wrong line count
    document.fonts?.ready.then(clamp);

    return () => {
      isCancelled = true;
      observer.disconnect();
    };
  }, [html, clampLines]);

  return (
    <div
      ref={mergeRefs(ref, containerRef)}
      className={cls(styles.richText, className)}
      dangerouslySetInnerHTML={{ __html: html }}
      {...rest}
    />
  );
}

export default observer(forwardRef(RichText));
